import React from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { BUTTON_VARIANTS, BUTTON_SIZES } from '../constants/theme';

/**
 * 按钮组件 - DecisionAI 2.0
 *
 * @param {string} variant - 按钮样式变体: 'primary', 'secondary', 'outline'
 * @param {string} size - 按钮尺寸: 'small', 'default', 'large', 'nav'
 * @param {boolean} withArrow - 是否显示箭头图标
 * @param {boolean} fullWidth - 是否占满容器宽度
 * @param {string} className - 自定义类名
 */
const Button = ({
  variant = 'primary',
  size = 'default',
  children,
  className = '',
  withArrow = false,
  fullWidth = false,
  disabled = false,
  ...props
}) => {
  // 基础样式 - 按照设计文档规范
  const baseStyles = "inline-flex items-center justify-center font-medium rounded-lg transition-all duration-300 ease-smooth focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50";

  // 按钮变体样式
  const variantStyles = {
    primary: 'bg-gradient-button text-white shadow-button hover:shadow-button-hover transform hover:-translate-y-0.5',
    secondary: 'bg-success-500 hover:bg-success-600 text-white shadow-button hover:shadow-button-hover transform hover:-translate-y-0.5',
    outline: 'bg-transparent text-white border-2 border-white/30 hover:bg-white/10 transform hover:-translate-y-0.5',
    white: 'bg-white text-primary-600 hover:bg-gray-50 shadow-button hover:shadow-button-hover transform hover:-translate-y-0.5',
    'outline-white': 'bg-transparent text-white border-2 border-white hover:bg-white hover:text-primary-600 transform hover:-translate-y-0.5',
  };

  // 按钮尺寸样式 - 优化手机端触摸体验
  const sizeStyles = {
    small: 'px-4 py-2 text-sm min-h-[44px]', // 确保最小触摸区域
    default: 'px-6 py-3 text-base min-h-[48px]',
    large: 'px-8 py-4 text-lg min-h-[52px]',
    nav: 'px-6 py-2.5 text-sm min-h-[44px]',
  };

  const variantClass = variantStyles[variant] || variantStyles.primary;
  const sizeClass = sizeStyles[size] || sizeStyles.default;

  // 处理禁用状态
  const disabledClass = disabled ? 'opacity-50 cursor-not-allowed' : '';

  // 处理全宽按钮
  const widthClass = fullWidth ? 'w-full' : '';

  // 动画配置
  const buttonAnimations = {
    primary: {
      whileHover: {
        scale: 1.05,
        y: -2,
        boxShadow: "0 10px 25px rgba(16, 185, 129, 0.4)",
        transition: { duration: 0.2, ease: "easeOut" }
      },
      whileTap: {
        scale: 0.95,
        transition: { duration: 0.1 }
      }
    },
    secondary: {
      whileHover: {
        scale: 1.05,
        y: -2,
        boxShadow: "0 10px 25px rgba(34, 197, 94, 0.4)",
        transition: { duration: 0.2, ease: "easeOut" }
      },
      whileTap: {
        scale: 0.95,
        transition: { duration: 0.1 }
      }
    },
    outline: {
      whileHover: {
        scale: 1.05,
        y: -2,
        borderColor: "rgba(255, 255, 255, 0.8)",
        backgroundColor: "rgba(255, 255, 255, 0.1)",
        transition: { duration: 0.2, ease: "easeOut" }
      },
      whileTap: {
        scale: 0.95,
        transition: { duration: 0.1 }
      }
    }
  };

  const currentAnimation = buttonAnimations[variant] || buttonAnimations.primary;

  return (
    <motion.button
      className={`
        ${baseStyles}
        ${variantClass}
        ${sizeClass}
        ${widthClass}
        ${disabledClass}
        ${className}
        relative overflow-hidden group
      `}
      disabled={disabled}
      {...(!disabled ? currentAnimation : {})}
      {...props}
    >
      {/* 发光效果 */}
      {!disabled && variant === 'primary' && (
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0"
          whileHover={{
            opacity: 1,
            x: ["0%", "100%"],
            transition: {
              opacity: { duration: 0.2 },
              x: { duration: 0.6, ease: "easeInOut" }
            }
          }}
        />
      )}

      {/* 按钮内容 */}
      <motion.div
        className="relative z-10 flex items-center justify-center"
        whileHover={withArrow ? { x: 2 } : undefined}
        transition={{ duration: 0.2 }}
      >
        {children}
        {withArrow && (
          <motion.div
            whileHover={{
              x: 4,
              rotate: [0, -10, 10, 0],
              transition: {
                x: { duration: 0.2 },
                rotate: { duration: 0.4 }
              }
            }}
          >
            <ArrowRightIcon className="w-5 h-5 ml-2" />
          </motion.div>
        )}
      </motion.div>
    </motion.button>
  );
};

export default Button;